/**
 * MONSTER TOWN — ERRANDS. DATA ONLY. Owned by the ERRANDS/NPC crew.
 *
 * Nothing in this file is engine. A new errand is an entry in `ERRANDS`, and
 * that is the whole change (`registerContent()` already spreads this array).
 *
 * ===========================================================================
 *  THE REFERENCE ERRAND — build this one first, it proves the whole system.
 * ===========================================================================
 * In the owner's words: "oh no, the zoo animals are hungry and they're out of
 * food — whip something up in the kitchen and bring it."
 *
 *   talk    the zookeeper flags you down            (a neighbour, out in town)
 *   gather  three pieces of fruit                   (garden / shop / a gift)
 *   make    a fruit bowl in YOUR OWN kitchen        (sequencing + counting)
 *   deliver carry it across town and feed them      (the payoff)
 *
 * Four step KINDS, three PLACES, one thread. That is the shape of the whole
 * expansion: the places stop being separate screens and start needing each other.
 *
 * Notice what the type system already guarantees about it:
 *  - `steps` is a non-empty tuple, so an errand with nothing to do will not compile;
 *  - there is no deadline field to set, so it cannot be failed or timed out;
 *  - `gather` is auto-satisfied from the bag, so a kid who already picked the
 *    fruit is never sent to pick it again.
 *
 * IDS REFERENCED HERE AND OWNED ELSEWHERE (all data, all a drop):
 *   npc_zookeeper            — NPC crew, `src/content/neighbours.ts`
 *   ing_fruit                — cooking crew, ingredient item id
 *   recipe_fruit_bowl        — cooking crew, `src/content/recipes.ts`
 *   dish_fruit_bowl          — what that recipe produces
 *   zoo_feeder               — zoo crew, the trough you tap to feed
 *   venue_zoo                — zoo crew, the scene id
 * Until those land the errand simply never gets offered, which is a quiet
 * no-op rather than a crash — `isOfferable` and the guide arrow both cope with
 * an id nothing has claimed yet.
 */

import { defineErrand, type ErrandDef } from '../core/errands'

/** THE REFERENCE ERRAND. */
export const zooHungryAnimals = defineErrand({
  id: 'errand_zoo_hungry',
  devName: 'The animals are hungry',
  giver: 'npc_zookeeper',
  icon: 'zoo',
  steps: [
    {
      kind: 'talk',
      npc: 'npc_zookeeper',
      icon: 'talk',
      at: 'town',
      voice: 'errand.zoo.hungry.ask',
    },
    {
      /*
       * `at` NAMES THE TREE, NOT THE GARDEN (fixed vr1 — this was a BLOCKER).
       *
       * `at: 'garden'` pointed the guide arrow at the nearest decorative flower
       * bed, 344 px from the only object in the game that gives you `ing_fruit`.
       * Fourteen hops of tapping exactly where the arrow said left `have` at
       * 0/3. A three-year-old has one instruction and it was a loop.
       *
       * `src/errands/targets.ts` now resolves a placed thing by def id, so this
       * is exact: the arrow lands on the fruit tree, three taps fill the step.
       * It is still just a word in a data file — if a town has no fruit tree the
       * key resolves to nothing and the arrow quietly stays down, exactly as it
       * did for every unshipped place before it.
       */
      kind: 'gather',
      item: 'ing_fruit',
      count: 3,
      from: 'garden',
      icon: 'fruit',
      at: 'cook_fruit_tree',
      voice: 'errand.zoo.hungry.gather',
    },
    {
      /*
       * `at` NAMES THE ROOM *AND THEN THE POT* (fixed vr2 — this was a MAJOR).
       *
       * `at: 'house_kitchen'` walked a kid across town and through two doors
       * perfectly, and then went silent the instant they arrived: the key IS
       * the scene they are now standing in, so the guide arrow had nothing to
       * point at. The kitchen holds twelve placed things and nothing on the
       * glass distinguished the pot. A pre-reader's one instruction — go where
       * the arrow is — ran out one object short of the goal.
       *
       * `>` chains two place words: the room while you are outside it, the pot
       * once you are in. `src/errands/targets.ts` walks the chain and takes the
       * first rung with somewhere to point. Still only words in a data file: a
       * town with no `cook_pot` falls through to the room, and a kid already in
       * the room gets the quiet no-arrow that shipped before.
       */
      kind: 'make',
      recipe: 'recipe_fruit_bowl',
      icon: 'bowl',
      at: 'house_kitchen>cook_pot',
      voice: 'errand.zoo.hungry.cook',
    },
    {
      // The same shape, and it was the same defect: inside the zoo, `venue_zoo`
      // is where you already are. The trough is the thing to tap, and it is the
      // id the step already names in `to`.
      kind: 'deliver',
      item: 'dish_fruit_bowl',
      to: 'zoo_feeder',
      icon: 'zoo',
      at: 'venue_zoo>zoo_feeder',
      voice: 'errand.zoo.hungry.feed',
    },
  ],
  reward: { coins: 40 },
  /**
   * Feeding time. The zookeeper only asks during the day, so the errand ARRIVES
   * as part of the world's rhythm rather than at random — and because it is
   * 'daily', it comes back round, which is the recurring feeding-time errand the
   * brief asks for. Missing a day costs nothing; it is simply offered again.
   */
  offer: { from: 'neighbour', fromHour: 8, toHour: 17 },
  repeat: 'daily',
})

/* ===========================================================================
 *  NEIGHBOUR REQUESTS — the lighter, build-shaped class of errand (brief §4)
 * ===========================================================================
 * *"could you put a bench by the pond?"*, *"I'd love a red roof"*, *"my garden
 * needs three flowers"*. Shown as pictures, checked against the real town, paid
 * in coins and a delighted neighbour. This is genuine follow-the-instructions
 * practice, and it is what a kid who does not know what to build can do next.
 *
 * THEY ARE ORDINARY `ErrandDef`s. There is no second engine and no second card:
 * a request is an errand whose steps are `build` steps, so everything the
 * errand system already does — never fails, never times out, several at once,
 * any order, one picture card — it does for free.
 *
 * THREE RULES THIS TABLE FOLLOWS, and a new row must follow them too:
 *
 *  1. ASK BY CATEGORY, NOT BY PIECE ID. `category: 'plant'` means *any* flower
 *     the kid owns, now or later. A `defId` would make the request stale the
 *     day the content crew renames a def, and it would tell a three-year-old
 *     that only one particular flower counts. THE ONE EXCEPTION is a request
 *     that is literally about one object — "a bench by the pond" means a bench,
 *     and `category: 'furniture'` would let a bookcase answer it.
 *  2. ASK FOR SOMETHING THEY CAN BUILD. `src/errands/requests.ts` will not let
 *     a request be offered unless the kid's palette already answers it, so an
 *     expensive category simply waits politely until they own one. Free-forever
 *     categories (`block`, `path`, `wall`, `floor`, `roof`, `fence`, `door`,
 *     `window`, `stair`, and `plant`/`tree` through their free ids) are the
 *     ones that can arrive on day one.
 *  3. START WITH THE TALK. The neighbour walks over and asks — that IS the
 *     `talk` step, and `src/errands/offers.ts` reports it the moment they hand
 *     the errand over. So the strip's first chip is already ticked when the
 *     card lands, which is a true picture of what just happened and teaches the
 *     kid what a ticked chip means before they have to earn one.
 */

/**
 * THE ONE FROM THE BRIEF, and the exception to rule 1: a bench means a bench.
 * `town_bench` costs 25 coins, so this request politely waits until the kid
 * owns one rather than asking for something they cannot place — which is the
 * "never stuck" rule doing its job, and it makes the shop worth visiting.
 */
export const benchByThePond = defineErrand({
  id: 'errand_bench_pond',
  devName: 'Could you put a bench by the pond?',
  giver: 'npc_neighbour_bramble',
  icon: 'bench',
  steps: [
    { kind: 'talk', npc: 'npc_neighbour_bramble', icon: 'talk', at: 'town' },
    { kind: 'build', defId: 'town_bench', count: 1, sceneId: 'town', icon: 'bench', at: 'pond' },
  ],
  reward: { coins: 30 },
  offer: { from: 'neighbour', afterDay: 2 },
})

/** "My garden needs three flowers." Free on day one — the counting one. */
export const threeFlowers = defineErrand({
  id: 'errand_three_flowers',
  devName: 'My garden needs three flowers',
  giver: 'npc_neighbour_tuffet',
  icon: 'plant',
  steps: [
    { kind: 'talk', npc: 'npc_neighbour_tuffet', icon: 'talk', at: 'town' },
    { kind: 'build', category: 'plant', count: 3, sceneId: 'town', icon: 'plant', at: 'garden' },
  ],
  reward: { coins: 24 },
  offer: { from: 'neighbour', fromHour: 8, toHour: 19 },
  repeat: 'once',
})

/** "A path up to my door." Paths auto-connect, so this one always looks good. */
export const pathToTheDoor = defineErrand({
  id: 'errand_path_home',
  devName: 'A path up to the door',
  giver: 'npc_neighbour_moss',
  icon: 'path',
  steps: [
    { kind: 'talk', npc: 'npc_neighbour_moss', icon: 'talk', at: 'town' },
    { kind: 'build', category: 'path', count: 4, sceneId: 'town', icon: 'path', at: 'house' },
  ],
  reward: { coins: 20 },
  offer: { from: 'neighbour', afterDay: 1 },
  repeat: 'once',
})

/** "I'd love a red roof." Straight from the brief; roof tiles are free. */
export const aRedRoof = defineErrand({
  id: 'errand_red_roof',
  devName: "I'd love a red roof",
  giver: 'npc_neighbour_pip',
  icon: 'roof',
  steps: [
    { kind: 'talk', npc: 'npc_neighbour_pip', icon: 'talk', at: 'town' },
    { kind: 'build', category: 'roof', count: 2, sceneId: 'town', icon: 'roof', at: 'house' },
  ],
  reward: { coins: 26 },
  offer: { from: 'neighbour', afterDay: 2 },
  repeat: 'once',
})

/** "Some trees along the road." Two of them — the easiest counting there is. */
export const treesByTheRoad = defineErrand({
  id: 'errand_two_trees',
  devName: 'Some trees along the road',
  giver: 'npc_neighbour_fen',
  icon: 'tree',
  steps: [
    { kind: 'talk', npc: 'npc_neighbour_fen', icon: 'talk', at: 'town' },
    { kind: 'build', category: 'tree', count: 2, sceneId: 'town', icon: 'tree', at: 'plaza' },
  ],
  reward: { coins: 22 },
  offer: { from: 'neighbour', afterDay: 3 },
  repeat: 'once',
})

/**
 * A NOTE IN THE MAILBOX, in the morning. Same errand shape, different arrival —
 * `from: 'mailbox'` is read by the offer system, not by the engine, so "how it
 * turns up" is data exactly like everything else here.
 */
export const fenceForTheYard = defineErrand({
  id: 'errand_fence_yard',
  devName: 'A fence around the yard, please',
  giver: 'npc_neighbour_nimbus',
  icon: 'fence',
  steps: [
    { kind: 'talk', npc: 'npc_neighbour_nimbus', icon: 'talk', at: 'town' },
    { kind: 'build', category: 'fence', count: 3, sceneId: 'town', icon: 'fence', at: 'house' },
  ],
  reward: { coins: 28 },
  offer: { from: 'mailbox', fromHour: 6, toHour: 12, afterDay: 2 },
  repeat: 'once',
})

/** Everything registered at boot. Add your errand to this array; that is all. */
export const ERRANDS: readonly ErrandDef[] = [
  zooHungryAnimals,
  benchByThePond,
  threeFlowers,
  pathToTheDoor,
  aRedRoof,
  treesByTheRoad,
  fenceForTheYard,
]
